import { studentLibraryAssignmentFilter } from '@breakoutlearning/firebase-repository/cubits/StudentLibraryCubit'
import type {
  StudentLibraryCubit,
  StudentLibraryAssignmentFilter,
  StudentLibraryAssignmentSortingStrategy,
} from '@breakoutlearning/firebase-repository/cubits/StudentLibraryCubit'
import {
  type ValidLibraryObject,
  ValidLibraryObjectActionState,
} from '@breakoutlearning/firebase-repository/stores/ValidLibraryObject'
import classNames from 'classnames'
import {
  BreakoutButton,
  BreakoutIconButton,
} from 'components/design-system/BreakoutButton'
import { BreakoutSelect } from 'components/design-system/BreakoutSelect'
import { BreakoutTable } from 'components/design-system/BreakoutTable'
import type { BreakoutTableColumnHeaderSortableBase } from 'components/design-system/BreakoutTableColumnHeader'
import { BreakoutTableColumnHeader } from 'components/design-system/BreakoutTableColumnHeader'
import { ChevronRight } from 'components/icons/ChevronRight'
import { CircleX } from 'components/icons/CircleX'
import { ClockIcon } from 'components/icons/Clock'
import { Search } from 'components/icons/Search'
import { SortIcon } from 'components/icons/Sort'
import { Spinner } from 'components/Spinner'
import { useRootStore } from 'hooks/rootStore'
import { DateTime } from 'luxon'
import { observer } from 'mobx-react-lite'
import { useCallback, type MouseEvent, useEffect, useMemo, useRef } from 'react'
import { EmptyListPlaceholder } from './EmptyListPlaceholder'
import {
  LibraryObjectState,
  SectionPaymentMethod,
} from '@breakoutlearning/firebase-repository/types'
import { ChatBubbleIcon } from 'components/icons/ChatBubble'
import { useTranslationTyped } from 'i18n/i18n'
import { useBreakoutUser } from 'hooks/profile'
import { useLTIMode } from 'hooks/use-lti-mode'
import { ScheduleIcon } from 'components/icons/Schedule'
import { BreakoutSearchInput } from 'components/design-system/BreakoutSearchInput'
import { useRepository } from 'hooks/auth'
import { useQueryParams } from 'hooks/router'
import { InternalLink } from 'components/core/InternalLink'
import type { QueryParams } from 'mobx-router'

const TableRow = ({
  libraryObject,
  cubit,
}: {
  libraryObject: ValidLibraryObject
  cubit: StudentLibraryCubit
}) => {
  const { ltiMode } = useLTIMode()
  const getFormattedTime = (date: Date) => {
    const dt = DateTime.fromJSDate(date, { zone: 'local' }).setZone(
      DateTime.local().zoneName
    )
    const dayStr = dt.toFormat('LLL d, yyyy')
    const timeStr =
      dt.toLocaleString(DateTime.TIME_SIMPLE) +
      ' ' +
      DateTime.local().toFormat('ZZZZ')
    return {
      dayStr,
      timeStr,
    }
  }
  const { dueDate } = libraryObject.assignment
  const scheduledAt = libraryObject.scheduledAtDate?.toJSDate()

  const dueDateFormatted = dueDate ? getFormattedTime(dueDate) : null

  const scheduledAtFormatted = scheduledAt
    ? getFormattedTime(scheduledAt)
    : null

  const assignmentLinkProps = useMemo(() => {
    const roomStateId = libraryObject.roomState.id
    return {
      to: roomStateId ? 'assignmentWithRoom' : 'assignment',
      params: {
        assignmentId: libraryObject.assignment.id,
        sectionId: libraryObject.section.id,
        roomStateId: libraryObject.roomState.id,
      },
    }
  }, [libraryObject])

  const roomsWithOutstandingPeerReview = cubit.roomsWithOutstandingPeerReview

  /* don't render rows for library objects that are still loading slide deck data */
  if (
    libraryObject.slideDeck.isLoading ||
    roomsWithOutstandingPeerReview.isLoading
  )
    return null

  const hasOutstandingPeerReview =
    roomsWithOutstandingPeerReview.value.includes(libraryObject.roomState.id)

  const canceledOrExpired = [
    LibraryObjectState.canceled,
    LibraryObjectState.expired,
  ].includes(libraryObject.libraryObjectState)

  const showChatButton =
    cubit.isLoaded &&
    !canceledOrExpired &&
    !libraryObject.requiresPayment &&
    libraryObject.roomState.isNotEmpty

  return (
    <tr
      data-testid={`library-object-list-item-${libraryObject.libraryObjectId}`}
    >
      {/* course (section) */}
      <TableRowClassNameCell
        libraryObject={libraryObject}
        linkProps={assignmentLinkProps}
      />
      {/* instructor */}
      <TableRowInstructorCell libraryObject={libraryObject} />
      {/* experience (slide deck logo/name/teaser) */}
      <TableRowExperienceCell
        libraryObject={libraryObject}
        linkProps={assignmentLinkProps}
      />
      {/* due date */}
      {!ltiMode && (
        <td
          className="border-b-1 border-outline-light py-4 !pl-5"
          data-testid={`library-duedate-${libraryObject.section.id}`}
        >
          {dueDateFormatted && (
            <div className="flex flex-col">
              <strong className="text-label-medium text-on-surface">
                {dueDateFormatted.dayStr}
              </strong>
              <strong className="text-body-medium text-on-surface-var">
                {dueDateFormatted.timeStr}
              </strong>
            </div>
          )}
        </td>
      )}
      {/* scheduledDate */}
      <td
        className="border-b-1 border-outline-light py-4 !pl-5"
        data-testid={`library-sessiondate-${libraryObject.section.id}`}
      >
        {scheduledAtFormatted && (
          <div className="flex flex-col">
            <strong className="text-label-medium text-on-surface">
              {scheduledAtFormatted.dayStr}
            </strong>
            <strong className="text-body-medium text-on-surface-var">
              {scheduledAtFormatted.timeStr}
            </strong>
          </div>
        )}
      </td>
      {
        /* chat */
        <td
          className="hidden border-b-1 border-outline-light py-4 !pl-5 lg:table-cell"
          data-testid={`library-chat-${libraryObject.section.id}`}
        >
          {showChatButton && (
            <div className="flex items-center justify-center">
              <ChatBubbleButton libraryObject={libraryObject} />
            </div>
          )}
        </td>
      }
      <td
        className="border-b-1 border-outline-light py-4 !pl-5"
        data-testid={`library-action-${libraryObject.section.id}`}
      >
        {/* pr-2 prevents the outline from peeking from under the sticky header */}
        <div className="flex h-full w-full items-center justify-center pr-2">
          <ActionForLibraryObject
            libraryObject={libraryObject}
            isHeader={false}
            actionState={libraryObject.getActionState({
              hasOutstandingPeerReview,
            })}
          />
        </div>
      </td>
    </tr>
  )
}

const TableRowClassNameCell = observer(function TableRowClassNameCell({
  libraryObject,
  linkProps,
}: {
  libraryObject: ValidLibraryObject
  linkProps: { to: string; params: QueryParams } | undefined
}) {
  const content = (
    <div className="flex flex-col justify-center break-words">
      <strong className="text-label-medium text-on-surface">
        {libraryObject.section.data.className}
      </strong>
      <strong className="text-body-medium text-on-surface-var">
        {libraryObject.section.data.sectionName}
      </strong>
    </div>
  )

  return (
    <th
      scope="row"
      className="border-b-1 border-outline-light py-4 !pl-5 text-left font-normal"
      data-testid={`library-course-${libraryObject.section.id}`}
    >
      {linkProps ? (
        <InternalLink to={linkProps.to} params={linkProps.params}>
          {content}
        </InternalLink>
      ) : (
        content
      )}
    </th>
  )
})

const TableRowInstructorCell = observer(function TableRowInstructorCell({
  libraryObject,
}: {
  libraryObject: ValidLibraryObject
}) {
  return (
    <td
      className="hidden border-b-1 border-outline-light py-4 !pl-5 lg:table-cell"
      data-testid={`library-instructor-${libraryObject.section.id}`}
    >
      <div className="flex flex-col justify-center">
        <strong className="text-label-medium">
          {libraryObject.section.instructor.fullName}
        </strong>
      </div>
    </td>
  )
})

function TableRowExperienceCell({
  libraryObject,
  linkProps,
}: {
  libraryObject: ValidLibraryObject
  linkProps: { to: string; params: QueryParams } | undefined
}) {
  const experienceContent = (
    <>
      <div className="aspect-square h-10 p-2">
        {libraryObject.slideDeckImageURL ? (
          <img
            src={libraryObject.slideDeckImageURL}
            className="max-h-full max-w-full object-cover"
            alt=""
          />
        ) : (
          <div className="h-full w-full bg-surface-dim" />
        )}
      </div>
      <div className="min-w-0 flex-1 flex-col break-words">
        <div className="text-label-medium leading-[1.05rem] text-on-surface">
          {libraryObject.slideDeckName}
        </div>
        <div className="text-body-medium leading-[1.05rem] text-on-surface-var">
          {libraryObject.slideDeckTeaser}
        </div>
      </div>
    </>
  )

  return (
    <td
      data-testid={`library-experience-${libraryObject.section.id}`}
      className="border-b-1 border-outline-light py-4"
    >
      {linkProps ? (
        <InternalLink
          to={linkProps.to}
          params={linkProps.params}
          className="flex flex-row items-center"
        >
          {experienceContent}
        </InternalLink>
      ) : (
        <div className="flex flex-row items-center">{experienceContent}</div>
      )}
    </td>
  )
}

export const AssignmentTable = observer(function AssignmentTable({
  cubit,
}: {
  cubit: StudentLibraryCubit
}) {
  const { tt } = useTranslationTyped()
  const { ltiMode } = useLTIMode()
  const repository = useRepository()
  const rootStore = useRootStore()
  const params = useQueryParams()
  const tableRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (params?.restoreScroll === 'true') {
      const scrollTop = repository.numberStore.get('libraryTableScrollTop')
      if (scrollTop) {
        requestAnimationFrame(() => {
          tableRef.current?.scrollTo(0, scrollTop)
        })
      }
      rootStore.updateQueryParams({
        restoreScroll: undefined,
      })
    }
  }, [params, params?.restoreScroll, repository.numberStore, rootStore])

  if (cubit.libraryObjects.length === 0) return <EmptyListPlaceholder />

  const libraryObjectList = cubit.filteredSortedLibraryObjects

  const sortable: BreakoutTableColumnHeaderSortableBase<StudentLibraryAssignmentSortingStrategy> =
    {
      changeSortingStrategy: (strategy, order) =>
        cubit.changeSortingStrategy(strategy, order),
      currentOrder: cubit.sortingOrder,
      currentSortingStrategy: cubit.sortingStrategy,
      allowDefaultSortingStrategy: true,
    }

  const hasAssignments = cubit.sortedLibraryObjects.length > 0
  const hasSearchTerm = cubit.assignmentFilterSearchTerm.length > 0

  return (
    <section
      aria-label={tt.library.filter_assignments()}
      className="flex min-w-[38rem] grow flex-col overflow-y-auto md:min-w-[52rem]"
    >
      <div className="flex w-full flex-row gap-4 p-1 pb-3.5">
        <p className="sr-only">{tt.library.search_filter_description()}</p>
        <BreakoutSearchInput
          name="filter"
          onChange={(value) => {
            cubit.changeSearchTerm(value.target.value)
          }}
          className="flex-grow"
          aria-label={tt.library.search_experiences()}
          placeholder={tt.library.search_experiences()}
          inputClassName="!text-body-medium outline-outline-light"
          allowClear
        />
        <FilterAssignmentsButton cubit={cubit} />
        <div className="sr-only" aria-live="assertive">
          {hasSearchTerm &&
            (libraryObjectList.length > 0
              ? tt.library.search_results_available({
                  count: libraryObjectList.length,
                })
              : tt.library.search_no_results_available())}
        </div>
      </div>
      <div
        className="grow overflow-y-auto overflow-x-hidden"
        onScroll={(e) => {
          repository.numberStore.set(
            'libraryTableScrollTop',
            e.currentTarget.scrollTop
          )
        }}
        ref={tableRef}
      >
        <BreakoutTable
          className={classNames('relative table-fixed', {
            'min-h-full': libraryObjectList.length === 0,
          })}
        >
          <thead className="sticky top-0 z-20 bg-surface text-body-small">
            <tr>
              <BreakoutTableColumnHeader
                className="w-[15%] lg:w-auto"
                sortable={{
                  sortingStrategy: 'sectionName',
                  ...sortable,
                }}
                label={tt.library.course()}
              />
              <BreakoutTableColumnHeader
                className="hidden lg:table-cell"
                sortable={{
                  sortingStrategy: 'instructorName',
                  ...sortable,
                }}
                label={tt.library.instructor()}
              />
              <BreakoutTableColumnHeader
                className="w-[25%] lg:w-auto"
                sortable={{
                  sortingStrategy: 'experienceName',
                  ...sortable,
                }}
                label={tt.library.experience()}
              />
              {!ltiMode && (
                <BreakoutTableColumnHeader
                  className="w-28 min-w-28"
                  sortable={{
                    sortingStrategy: 'dueDate',
                    ...sortable,
                  }}
                  label={tt.library.due_date()}
                />
              )}
              <BreakoutTableColumnHeader
                className="w-28 min-w-28"
                sortable={{
                  sortingStrategy: 'scheduledAt',
                  ...sortable,
                }}
                label={tt.library.session_date()}
              />
              <BreakoutTableColumnHeader
                className="hidden w-[5.5rem] min-w-[5.5rem] lg:table-cell"
                label={tt.library.chat()}
                alignment="center"
              />
              <BreakoutTableColumnHeader
                label={tt.library.action()}
                alignment="center"
              />
              {/* Update EmptyListState if changing the number of columns */}
            </tr>
          </thead>
          <tbody data-testid="library-object-list" className="overflow-y-auto">
            {!hasAssignments && <NoAssignmentsEmptyState />}
            {hasAssignments &&
              libraryObjectList.map((libraryObject) => (
                <TableRow
                  key={libraryObject.libraryObjectId}
                  libraryObject={libraryObject}
                  cubit={cubit}
                />
              ))}
            {hasAssignments &&
              libraryObjectList.length === 0 &&
              (hasSearchTerm ? (
                <EmptySearchState />
              ) : (
                <NoCurrentAssignmentsEmptyState />
              ))}
          </tbody>
        </BreakoutTable>
      </div>
    </section>
  )
})

function EmptySearchState() {
  const { tt } = useTranslationTyped()
  return (
    <tr>
      <td colSpan={7}>
        <div className="flex flex-col items-center justify-center gap-2 rounded-2xl p-7">
          <Search className="stroke-fixed-accent-color" size={50} />
          <div className="my-1 text-title-large text-on-surface">
            {tt.instructor_library.no_results_found()}
          </div>
          <div className="text-center text-body-large text-on-surface-var">
            {tt.instructor_library.no_results_found_description_1()}
          </div>
        </div>
      </td>
    </tr>
  )
}

function NoCurrentAssignmentsEmptyState() {
  const { tt } = useTranslationTyped()
  return (
    <tr>
      <td colSpan={7}>
        <div className="flex flex-col items-center justify-center gap-2 rounded-2xl p-7">
          <div className="my-1 text-title-large text-on-surface">
            {tt.student_library.no_current_assignments()}
          </div>
          <div className="text-center text-body-large text-on-surface-var">
            {tt.student_library.no_current_assignments_description_1()}
          </div>
        </div>
      </td>
    </tr>
  )
}

function NoAssignmentsEmptyState() {
  const { tt } = useTranslationTyped()
  return (
    <tr>
      <td colSpan={7}>
        <div className="flex flex-col items-center justify-center gap-2 rounded-2xl p-7">
          <div className="my-1 text-title-large text-on-surface">
            {tt.student_library.no_assignments()}
          </div>
          <div className="text-center text-body-large text-on-surface-var">
            {tt.student_library.no_assignments_description_1()}
          </div>
        </div>
      </td>
    </tr>
  )
}

const ChatBubbleButton = observer(function ChatBubbleButton({
  libraryObject,
}: {
  libraryObject: ValidLibraryObject
}) {
  const rootStore = useRootStore()
  const { tt } = useTranslationTyped()

  const user = useBreakoutUser()
  const notificationsCount =
    user.notifications.getChatNotificationsForRoomCount(
      libraryObject.roomState.id
    )

  return (
    <BreakoutIconButton
      kind="secondary"
      size="small"
      className="shrink-0"
      icon={<ChatBubbleIcon size={12} />}
      badgeCount={notificationsCount || undefined}
      badgeSrLabel={tt.library.unread_messages()}
      aria-label={tt.library.chat()}
      onClick={(e) => {
        e.stopPropagation()
        rootStore.navigateTo(
          'assignmentWithRoom',
          {
            assignmentId: libraryObject.assignment.id,
            sectionId: libraryObject.section.id,
            roomStateId: libraryObject.roomState.id,
          },
          {
            action: 'chat',
          }
        )
      }}
    />
  )
})

const Label = ({
  text,
  type,
}: {
  text: string
  type: StudentLibraryAssignmentFilter
}) => {
  return (
    <div
      className={classNames(
        'flex flex-row items-center gap-1 whitespace-nowrap px-4 py-2 text-label-medium'
      )}
    >
      <div
        className={classNames('h-2 min-h-2 w-2 min-w-2 rounded-full', {
          'bg-core-success': type === 'current',
          'bg-fixed-orange': type === 'all',
        })}
      ></div>
      <span>{text}</span>
    </div>
  )
}

const SelectedLabel = ({ text }: { text: string }) => {
  const { tt } = useTranslationTyped()
  return (
    <div className="flex flex-row items-center justify-center gap-4">
      <span>{tt.library.filter_prefix({ text })}</span>
      <SortIcon size={15} />
    </div>
  )
}

const DEFAULT_FILTER: StudentLibraryAssignmentFilter = 'current'

const FilterAssignmentsButton = observer(function FilterAssignmentsButton({
  cubit,
}: {
  cubit: StudentLibraryCubit
}) {
  const { tt } = useTranslationTyped()
  const store = useRootStore()

  const filterFromUrl = store.router.queryParams?.filter

  const filterFromUrlIsInvalid =
    typeof filterFromUrl !== 'string' ||
    !studentLibraryAssignmentFilter.includes(
      filterFromUrl as StudentLibraryAssignmentFilter
    )

  const filter = filterFromUrlIsInvalid
    ? DEFAULT_FILTER
    : (filterFromUrl as StudentLibraryAssignmentFilter)

  useEffect(() => {
    // When the page mounts, set the filter appropriately
    cubit.changeAssignmentFilter(filter)
  }, [cubit, filter])

  const handleFilterChange = useCallback(
    (value: StudentLibraryAssignmentFilter) => {
      store.updateQueryParams({ filter: value }, { replaceHistory: true })
      cubit.changeAssignmentFilter(value)
    },
    [store, cubit]
  )

  return (
    <BreakoutSelect<StudentLibraryAssignmentFilter>
      className="min-w-[215px]"
      kind="tertiary"
      value={filter}
      onChange={handleFilterChange}
      aria-label={tt.library.filter_assignments()}
      showChevron={false}
      testId="assignments-filter"
      options={[
        {
          label: (
            <Label text={tt.library.current_assignments()} type="current" />
          ),
          selectedLabel: (
            <SelectedLabel text={tt.library.current_assignments()} />
          ),
          value: 'current',
        },
        {
          label: <Label text={tt.library.all_assignments()} type="all" />,
          selectedLabel: <SelectedLabel text={tt.library.all_assignments()} />,
          value: 'all',
        },
      ]}
    />
  )
})

const ActionForLibraryObject = observer(function ActionForLibraryObject({
  libraryObject,
  isHeader,
  actionState,
}: {
  libraryObject: ValidLibraryObject
  isHeader: boolean
  actionState: ValidLibraryObjectActionState
}) {
  const store = useRootStore()
  const { tt } = useTranslationTyped()

  const navigateWithAction = useCallback(
    (
      actionState: ValidLibraryObjectActionState,
      event: MouseEvent<HTMLButtonElement>
    ) => {
      event.stopPropagation()
      const roomStateId = libraryObject.roomState.id
      const route = roomStateId ? 'assignmentWithRoom' : 'assignment'
      store.navigateTo(
        route,
        {
          assignmentId: libraryObject.assignment.id,
          sectionId: libraryObject.section.id,
          roomStateId: libraryObject.roomState.id,
        },
        {
          action: actionState.toString(),
        }
      )
    },
    [libraryObject, store]
  )

  switch (actionState) {
    case ValidLibraryObjectActionState.loading: {
      return <Spinner size={1.875} />
    }
    case ValidLibraryObjectActionState.enroll: {
      const { priceInDollars } = libraryObject.slideDeck

      const buttonText = (() => {
        switch (libraryObject.section.sectionPaymentMethodWithDefault) {
          case SectionPaymentMethod.token:
            return tt.student_library.enroll_price({ price: priceInDollars })
          case SectionPaymentMethod.sectionPassStripe:
            return tt.student_library.enroll_with_section_pass()
          case SectionPaymentMethod.sectionPassCoupon:
            return tt.student_library.enroll_with_section_pass()
          case SectionPaymentMethod.sectionPassStripeCoupon:
            return tt.student_library.enroll_with_section_pass()
          case SectionPaymentMethod.sectionPassCodelessAccess:
            return tt.student_library.enroll_with_section_pass()
        }
      })()

      return (
        <BreakoutButton
          size={!isHeader ? 'small' : 'medium'}
          onClick={(e) => {
            navigateWithAction(ValidLibraryObjectActionState.enroll, e)
          }}
        >
          {buttonText}
        </BreakoutButton>
      )
    }
    case ValidLibraryObjectActionState.availableOn: {
      const displayedDateTime = libraryObject.assignment.formattedAssignedAt

      const linkLabel = (() => {
        switch (libraryObject.libraryObjectState) {
          case LibraryObjectState.enrolled:
            return libraryObject.assignment.data.forbidStudentGroupSelection
              ? null
              : tt.library.actions.pre_schedule_session()
          case LibraryObjectState.mustSchedule:
            return tt.library.actions.pre_schedule_session()
          case LibraryObjectState.scheduled:
          case LibraryObjectState.readyToStart:
            return libraryObject.assignment.data.forbidStudentGroupScheduling
              ? null
              : tt.library.actions.reschedule_session()
          default:
            return null
        }
      })()

      return (
        <div className="flex flex-col items-center justify-center gap-1 text-center">
          <strong className="text-label-medium">
            {tt.library.available_date({ date: displayedDateTime })}
          </strong>
          {linkLabel && (
            <InternalLink
              to={
                libraryObject.roomState.id ? 'assignmentWithRoom' : 'assignment'
              }
              params={{
                assignmentId: libraryObject.assignment.id,
                sectionId: libraryObject.section.id,
                roomStateId: libraryObject.roomState.id,
              }}
              className="text-body-small text-on-surface-var underline"
            >
              {linkLabel}
            </InternalLink>
          )}
        </div>
      )
    }

    case ValidLibraryObjectActionState.joinGroup: {
      const joinForbidden =
        libraryObject.assignment.data.forbidStudentGroupSelection
      return (
        <BreakoutButton
          kind={isHeader ? 'primary' : 'secondary'}
          size={!isHeader ? 'small' : 'medium'}
          data-testid={`join-group-button-${libraryObject.assignment.id}`}
          disabled={joinForbidden}
          tooltip={
            joinForbidden
              ? tt.student_assignment.join_group_disabled_by_instructor()
              : undefined
          }
          onClick={(e) => {
            navigateWithAction(actionState, e)
          }}
        >
          {tt.library.actions.join_group()}
        </BreakoutButton>
      )
    }
    case ValidLibraryObjectActionState.joinNewGroup: {
      return (
        <BreakoutButton
          kind={isHeader ? 'primary' : 'secondary'}
          size={!isHeader ? 'small' : 'medium'}
          data-testid={`join-new-group-button-${libraryObject.assignment.id}`}
          onClick={(e) => {
            navigateWithAction(actionState, e)
          }}
        >
          {tt.library.actions.join_new_group()}
        </BreakoutButton>
      )
    }
    case ValidLibraryObjectActionState.pendingScheduling:
    case ValidLibraryObjectActionState.pendingGrouping: {
      return (
        <div className="flex flex-col items-center justify-center gap-1 text-center">
          <ClockIcon size={15} className="stroke-fixed-accent-color" />
          <strong className="text-label-small text-on-surface">
            {actionState === ValidLibraryObjectActionState.pendingScheduling
              ? tt.library.actions.pending_scheduling()
              : tt.library.actions.pending_grouping()}
          </strong>
        </div>
      )
    }
    case ValidLibraryObjectActionState.scheduleSession: {
      const schedulingForbidden =
        libraryObject.assignment.data.forbidStudentGroupScheduling
      return (
        <BreakoutButton
          kind={isHeader ? 'primary' : 'secondary'}
          size={!isHeader ? 'small' : 'medium'}
          disabled={schedulingForbidden}
          tooltip={
            schedulingForbidden
              ? tt.student_assignment.schedule_group_disabled_by_instructor()
              : undefined
          }
          onClick={(e) => {
            navigateWithAction(ValidLibraryObjectActionState.scheduleSession, e)
          }}
        >
          {tt.library.actions.schedule_session()}
        </BreakoutButton>
      )
    }
    case ValidLibraryObjectActionState.completeQuiz: {
      return (
        <BreakoutButton
          kind={isHeader ? 'primary' : 'secondary'}
          size={!isHeader ? 'small' : 'medium'}
          onClick={(e) => {
            navigateWithAction(actionState, e)
          }}
        >
          {tt.library.actions.complete_quiz()}
        </BreakoutButton>
      )
    }
    case ValidLibraryObjectActionState.sessionScheduled: {
      return (
        <div className="flex flex-col items-center justify-center gap-1 text-center">
          <ScheduleIcon size={15} />
          <strong className="text-center text-label-small text-on-surface">
            {tt.library.actions.scheduled()}
          </strong>
        </div>
      )
    }
    case ValidLibraryObjectActionState.joinSession: {
      return (
        <BreakoutButton
          kind="primary"
          size={!isHeader ? 'small' : 'medium'}
          onClick={(e) => {
            navigateWithAction(actionState, e)
          }}
        >
          {tt.library.actions.join_session()}
        </BreakoutButton>
      )
    }
    case ValidLibraryObjectActionState.suspended: {
      return (
        <BreakoutButton
          kind="primary"
          size={!isHeader ? 'small' : 'medium'}
          onClick={(e) => {
            navigateWithAction(actionState, e)
          }}
        >
          {tt.library.actions.continue_session()}
        </BreakoutButton>
      )
    }
    case ValidLibraryObjectActionState.startSession: {
      return (
        <BreakoutButton
          size={!isHeader ? 'small' : 'medium'}
          kind="primary"
          onClick={(e) => {
            navigateWithAction(actionState, e)
          }}
        >
          {tt.library.actions.start_session()}
        </BreakoutButton>
      )
    }
    case ValidLibraryObjectActionState.viewResults: {
      return (
        <BreakoutButton
          icon={<ChevronRight size={15} />}
          iconOnRight={true}
          size={!isHeader ? 'small' : 'medium'}
          kind={isHeader ? 'primary' : 'tertiary'}
          onClick={(e) => {
            navigateWithAction(actionState, e)
          }}
        >
          {tt.library.actions.view_results()}
        </BreakoutButton>
      )
    }
    case ValidLibraryObjectActionState.completePeerReviews: {
      return (
        <BreakoutButton
          size={!isHeader ? 'small' : 'medium'}
          kind={isHeader ? 'accent' : 'primary'}
          onClick={(e) => {
            navigateWithAction(actionState, e)
          }}
        >
          {tt.library.actions.start_peer_reviews()}
        </BreakoutButton>
      )
    }
    case ValidLibraryObjectActionState.experienceExpired: {
      return (
        <div className="flex flex-col items-center justify-center gap-1 text-center">
          <CircleX
            size={15}
            className="stroke-core-error"
            role="img"
            aria-label={tt.library.actions.experience_expired()}
          />
          <strong className="text-body-medium text-core-error">
            {tt.library.actions.experience_expired()}
          </strong>
        </div>
      )
    }
    case ValidLibraryObjectActionState.canceled: {
      return (
        <div className="flex flex-col items-center justify-center gap-1 text-center">
          <CircleX
            size={15}
            className="stroke-core-error"
            role="img"
            aria-label={tt.library.actions.canceled()}
          />
          <strong className="text-body-medium text-core-error">
            {tt.library.actions.canceled()}
          </strong>
        </div>
      )
    }
  }
})

export const PriorityActionHeader = observer(function PriorityActionHeader({
  libraryObject,
  actionState,
}: {
  libraryObject: ValidLibraryObject
  actionState: ValidLibraryObjectActionState
}) {
  const { t, tt } = useTranslationTyped()
  return (
    <div
      role="group"
      aria-label={t(`library.actions.header.${actionState}`)}
      className="mb-3 flex w-full flex-row items-center gap-5 rounded-[20px] bg-surface px-7 py-4"
    >
      <div className="flex flex-col justify-center">
        <h2 className="text-body-medium text-on-surface-var">
          {tt.library.next_action()}
        </h2>
        <strong className="text-title-large">
          {t(`library.actions.header.${actionState}`)}
        </strong>
      </div>
      <div className="w-1 min-w-1 self-stretch bg-fixed-accent-color"></div>
      <div className="flex min-w-[150px] flex-col text-body-medium">
        <strong>{libraryObject.section.data.className}</strong>
        <strong className="text-on-surface-var">
          {libraryObject.instructor.fullName}
        </strong>
      </div>
      <div className="flex flex-grow flex-row gap-1">
        {libraryObject.slideDeckImageURL && (
          <div className="aspect-square h-10 w-10 p-2">
            <img
              src={libraryObject.slideDeckImageURL}
              className="h-full w-full object-cover"
              alt=""
            />
          </div>
        )}
        <div className="flex flex-col justify-center">
          <strong className="line-clamp-1 text-body-medium text-on-surface-var">
            {libraryObject.slideDeck.data.slideDeckName}
          </strong>
          {libraryObject.slideDeck.data.slideDeckTeaser && (
            <strong className="line-clamp-1 text-label-large">
              {libraryObject.slideDeck.data.slideDeckTeaser}
            </strong>
          )}
        </div>
      </div>
      <div className="flex h-full items-center justify-center">
        <ActionForLibraryObject
          libraryObject={libraryObject}
          isHeader={true}
          actionState={actionState}
        />
      </div>
    </div>
  )
})
